import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Trophy } from 'lucide-react';
import {
  custoEfetivo,
  diferencaParaMelhorBps,
  formatarPct,
  ordenarPorCustoEfetivo,
  type LinhaComparativo,
} from '@profitlens/calculo';
import { Dias, Quantidade, Reais, Texto, DataBR } from './Valor';
import { MarcaDeOrigem } from './Origem';
import type { RespostaCotacao } from '../tipos';

/**
 * O comparativo existe para revelar uma coisa: o MENOR PRECO RARAMENTE E O
 * MENOR CUSTO.
 *
 * Um fornecedor R$ 1,40 mais barato por peca que exige o dobro da quantidade
 * minima e cobra frete sai mais caro na conta final. Por isso a ordenacao e
 * pelo custo unitario EFETIVO — que ja considera a quantidade que sera
 * realmente comprada (RN-046) — e nao pelo preco de tabela.
 *
 * Duas decisoes de exibicao que nao sao enfeite:
 *
 * - Campo nao informado aparece como `n/i`, nunca como R$ 0,00 (RN-044).
 *   Fornecedor que nao disse o frete nao cobra frete zero.
 * - Linha incompleta vai para o FIM e nao recebe posicao. Ela nao e "a mais
 *   cara", e a desconhecida — coloca-la no meio da ordenacao sugeriria uma
 *   colocacao que o dado nao sustenta.
 */

export interface OrigemLinha {
  id: string;
  produtoId: string;
  produtoNome: string;
  fornecedorId: string;
  fornecedorNome: string;
  unidade: string | null;
  /** Quantidade que se PRETENDE comprar. A mínima do fornecedor pode elevá-la. */
  quantidadeDesejadaMilli: number;
  resposta: RespostaCotacao;
  /** Para onde a linha leva ao ser clicada. */
  caminho?: string;
}

const COLUNAS = [
  'Produto',
  'Fornecedor',
  'P. unit.',
  'Qtd. mín.',
  'Disponível',
  'Frete',
  'Impostos',
  'Prazo',
  'Condição',
  'Validade',
  'Custo total',
  'Custo unit. efetivo',
  'Respondida em',
] as const;

export function TabelaComparativo({ origens }: { origens: OrigemLinha[] }) {
  const { linhas, diferencas } = useMemo(() => {
    const brutas: LinhaComparativo<OrigemLinha>[] = origens.map((o) => ({
      origem: o,
      custo: custoEfetivo(o.resposta, o.quantidadeDesejadaMilli),
    }));
    const ordenadas = ordenarPorCustoEfetivo(brutas);
    return { linhas: ordenadas, diferencas: diferencaParaMelhorBps(ordenadas) };
  }, [origens]);

  if (linhas.length === 0) return null;

  const completas = linhas.filter((l) => l.custo.completo).length;
  const incompletas = linhas.length - completas;

  return (
    <div className="flex flex-col gap-2">
      {/*
        A primeira coluna fica presa: rolando na horizontal no celular, o nome
        do produto continua visível — sem ele a linha vira uma fileira de
        números sem dono.
      */}
      <div className="overflow-x-auto rounded-md border border-regua-2">
        <table className="w-full min-w-[64rem] border-collapse bg-superficie text-sm">
          <thead>
            <tr>
              {COLUNAS.map((c, i) => (
                <th
                  key={c}
                  className={`border-b border-regua-2 px-3 py-2 font-mono text-[0.62rem] font-semibold tracking-wider text-marca uppercase ${
                    i === 0
                      ? 'sticky left-0 z-10 bg-superficie text-left'
                      : i >= 2 && i <= 6
                        ? 'text-right'
                        : 'text-left'
                  }`}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha, i) => {
              const o = linha.origem;
              const daIA = o.resposta.origem === 'IA';
              const melhor = linha.custo.completo && i === 0;
              const diferenca = diferencas[i];

              return (
                <tr
                  key={o.id}
                  className={`border-b border-regua last:border-0 ${
                    daIA ? 'bg-ia/8' : 'even:bg-marca/4'
                  } ${linha.custo.completo ? '' : 'opacity-75'}`}
                >
                  {/*
                    A coluna presa precisa de fundo OPACO: o resto da linha
                    passa por baixo dela ao rolar. Por isso o tom da IA entra
                    como camada por cima do fundo solido, e nao no lugar dele —
                    `bg-ia/8` sozinho deixaria os numeros vazarem atraves.
                  */}
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-superficie px-3 py-2 text-left font-medium"
                  >
                    {daIA && (
                      <span className="pointer-events-none absolute inset-0 bg-ia/8" aria-hidden />
                    )}
                    <span className="relative">
                      {o.caminho ? (
                        <Link to={o.caminho} className="hover:text-marca">
                          {o.produtoNome}
                        </Link>
                      ) : (
                        o.produtoNome
                      )}
                    </span>
                  </th>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <Link to={`/fornecedores/${o.fornecedorId}`} className="hover:text-marca">
                        {o.fornecedorNome}
                      </Link>
                      {daIA && <MarcaDeOrigem origem={o.resposta.origem} />}
                      {o.resposta.requerRevisao && (
                        <AlertTriangle
                          className="size-3.5 text-pend"
                          aria-label="Requer revisão"
                        />
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Reais cents={o.resposta.precoUnitarioCents} />
                    {daIA && <span className="text-ia" aria-hidden>*</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Quantidade milli={o.resposta.quantidadeMinimaMilli} />
                    {linha.custo.completo && linha.custo.minimoForcouAumento && (
                      <div
                        className="text-[0.65rem] text-c-res"
                        title="O mínimo deste fornecedor é maior que a quantidade desejada — a conta usa o mínimo dele."
                      >
                        obriga a comprar mais
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Quantidade milli={o.resposta.quantidadeDisponivelMilli} unidade={o.unidade} />
                    {linha.custo.completo && linha.custo.estoqueInsuficiente && (
                      <div className="text-[0.65rem] text-pend" title="Não há disponível o quanto seria comprado.">
                        insuficiente
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Reais cents={o.resposta.freteCents} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Reais cents={o.resposta.impostosCents} />
                  </td>
                  <td className="px-3 py-2">
                    <Dias dias={o.resposta.prazoEntregaDias} />
                  </td>
                  <td className="px-3 py-2 text-tinta-2">
                    <Texto valor={o.resposta.condicaoPagamento} />
                  </td>
                  <td className="px-3 py-2">
                    <DataBR iso={o.resposta.validade} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {linha.custo.completo ? (
                      <Reais cents={linha.custo.custoTotalCents} />
                    ) : (
                      <SemConta faltando={linha.custo.faltando} />
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {linha.custo.completo ? (
                      <div className="flex flex-col items-end">
                        <span className="inline-flex items-center gap-1 font-semibold">
                          {melhor && (
                            <Trophy className="size-3.5 text-ok" aria-label="Menor custo efetivo" />
                          )}
                          <Reais cents={linha.custo.custoUnitarioEfetivoCents} />
                        </span>
                        {diferenca !== null && diferenca !== undefined && diferenca > 0 && (
                          <span className="text-[0.65rem] text-tinta-3">
                            +{formatarPct(diferenca)} vs. o melhor
                          </span>
                        )}
                      </div>
                    ) : (
                      <SemConta faltando={linha.custo.faltando} />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <DataBR iso={o.resposta.dataResposta} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/*
        RN-050: valor extraido por IA nunca aparece sozinho. O texto que o
        fornecedor escreveu e o grau de confianca vem junto, para que dê para
        conferir o numero contra a frase que o originou.
      */}
      {linhas.some((l) => l.origem.resposta.origem === 'IA') && (
        <details className="rounded-md border border-ia/30 bg-ia/8 px-3 py-2">
          <summary className="cursor-pointer text-xs font-medium text-ia">
            Ver o que o fornecedor escreveu nas linhas lidas por IA
          </summary>
          <ul className="mt-2 flex flex-col gap-2">
            {linhas
              .filter((l) => l.origem.resposta.origem === 'IA')
              .map((l) => (
                <li key={l.origem.id} className="text-xs">
                  <span className="font-medium">{l.origem.fornecedorNome}</span>
                  {l.origem.resposta.confianca !== null && (
                    <span className="ml-1 text-tinta-3">
                      confiança <span className="num">{l.origem.resposta.confianca.toFixed(2)}</span>
                    </span>
                  )}
                  {l.origem.resposta.requerRevisao && (
                    <span className="ml-1 text-pend">· requer revisão</span>
                  )}
                  <blockquote className="mt-0.5 border-l-2 border-ia/40 pl-2 text-tinta-2 italic">
                    {l.origem.resposta.textoOriginal ?? 'texto original não guardado'}
                  </blockquote>
                </li>
              ))}
          </ul>
        </details>
      )}

      <p className="text-xs text-tinta-3">
        <strong className="text-tinta-2">n/i</strong> = não informado pelo fornecedor —{' '}
        <strong className="text-tinta-2">não</strong> é tratado como zero. O custo unitário efetivo
        usa a quantidade que será realmente comprada, respeitando a quantidade mínima de cada
        fornecedor.
        {incompletas > 0 && (
          <>
            {' '}
            {incompletas === 1 ? 'Uma linha ficou' : `${incompletas} linhas ficaram`} sem conta por
            falta de dado e {incompletas === 1 ? 'foi' : 'foram'} para o fim: desconhecido não é o
            mais caro.
          </>
        )}
      </p>
    </div>
  );
}

/** Diz O QUE falta, em vez de mostrar um trace vazio. */
function SemConta({ faltando }: { faltando: string[] }) {
  return (
    <span
      className="font-sans text-xs text-tinta-3 italic"
      title={`Sem conta: falta ${faltando.join(', ')}.`}
    >
      falta {faltando.join(', ')}
    </span>
  );
}
